import React, { useState } from "react";
import {
  Text,
  RadioInput,
  CheckboxInput,
  FilledButton,
  OutlinedButton,
  theme,
} from "@ifgengineering/component-library";
import styled from "styled-components";
import ModalWindow from "@components/ModalWindow";
import { TaxBenefit, InvestmentStatus } from "./types";
import { INVESTMENT_STATUS_CONFIG, DEFAULT_STATUS_COLORS } from "./constants";

export interface EisFundFilterValues {
  taxBenefit: TaxBenefit | "all";
  investmentStatuses: InvestmentStatus[];
}

interface EisFundFilterModalProps {
  show: boolean;
  onClose: () => void;
  filters: EisFundFilterValues;
  onApply: (filters: EisFundFilterValues) => void;
  onReset: () => void;
}

const TAX_BENEFIT_OPTIONS: Array<{ value: TaxBenefit | "all"; label: string }> =
  [
    { value: "all", label: "All" },
    { value: "EIS", label: "SEIS/EIS" },
    { value: "None", label: "None" },
  ];

const STATUS_OPTIONS: InvestmentStatus[] = [
  "NEW INVESTMENT",
  "PRE-PRODUCT-MARKET FIT",
  "BREAKOUT POTENTIAL",
  "SUCCESS UNLIKELY",
  "WRITE-OFF",
];

const EisFundFilterModal: React.FC<EisFundFilterModalProps> = ({
  show,
  onClose,
  filters,
  onApply,
  onReset,
}) => {
  const [localFilters, setLocalFilters] =
    useState<EisFundFilterValues>(filters);

  // Sync local filters with props when modal opens
  React.useEffect(() => {
    if (show) {
      setLocalFilters(filters);
    }
  }, [show, filters]);

  const handleTaxBenefitChange = (value: TaxBenefit | "all") => {
    setLocalFilters((prev) => ({ ...prev, taxBenefit: value }));
  };

  const handleStatusToggle = (status: InvestmentStatus) => {
    setLocalFilters((prev) => {
      const newStatuses = prev.investmentStatuses.includes(status)
        ? prev.investmentStatuses.filter((s) => s !== status)
        : [...prev.investmentStatuses, status];
      return { ...prev, investmentStatuses: newStatuses };
    });
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    const defaultFilters: EisFundFilterValues = {
      taxBenefit: "all",
      investmentStatuses: [],
    };
    setLocalFilters(defaultFilters);
    onReset();
  };

  return (
    <ModalWindow show={show} onClose={onClose}>
      <FilterModalContainer>
        <Text type="T24" color="SLATE900" fontFamily="archiasemibold">
          Filter by:
        </Text>

        <FilterSection>
          <Text type="S14" color="SLATE800">
            Tax Benefits
          </Text>
          <RadioInputGroup>
            {TAX_BENEFIT_OPTIONS.map((option) => {
              const isChecked = localFilters.taxBenefit === option.value;
              return (
                <RadioInputContainer
                  key={option.value}
                  $checked={isChecked}
                  onClick={() => handleTaxBenefitChange(option.value)}
                >
                  <RadioInput
                    value={option.value}
                    label={option.label}
                    labelFontSize="P16"
                    checked={isChecked}
                    onChange={() => {}}
                  />
                </RadioInputContainer>
              );
            })}
          </RadioInputGroup>
        </FilterSection>

        <FilterSection>
          <Text type="S14" color="SLATE800">
            Status
          </Text>
          <CheckboxInputGroup>
            {STATUS_OPTIONS.map((status) => {
              const config =
                INVESTMENT_STATUS_CONFIG[status] || DEFAULT_STATUS_COLORS;
              const isChecked =
                localFilters.investmentStatuses.includes(status);
              return (
                <CheckboxInputContainer key={status}>
                  <CheckboxInput
                    value={status}
                    label=""
                    labelSize="P16"
                    checked={isChecked}
                    onChange={() => handleStatusToggle(status)}
                  />
                  <StatusBadge
                    $backgroundColor={config.backgroundColor}
                    $textColor={config.textColor}
                  >
                    {config.label || status}
                  </StatusBadge>
                </CheckboxInputContainer>
              );
            })}
          </CheckboxInputGroup>
        </FilterSection>

        <ButtonGroupContainer>
          <OutlinedButton height="48px" text="Reset" onClick={handleReset} />
          <FilledButton
            height="48px"
            text="Apply Filters"
            color="blue"
            onClick={handleApply}
          />
        </ButtonGroupContainer>
      </FilterModalContainer>
    </ModalWindow>
  );
};

export default EisFundFilterModal;

// Styled Components
const FilterModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 400px;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RadioInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioInputContainer = styled.button<{ $checked?: boolean }>`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1.5px solid
    ${({ $checked }) =>
      $checked ? theme.colors.BLUE600 : theme.colors.SLATE300};
  background-color: ${theme.colors.WHITE};
  width: fit-content;
  cursor: pointer;

  &:hover {
    border-color: ${theme.colors.BLUE400};
  }
`;

const CheckboxInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckboxInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0px;
  border-radius: 8px;
  background-color: ${theme.colors.WHITE};
  width: fit-content;
`;

const StatusBadge = styled.span<{
  $backgroundColor: string;
  $textColor: string;
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $textColor }) => $textColor};
`;

const ButtonGroupContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;
